import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import { formatClientLocationSubtitle } from '../../utils/projectSubtitle'
import PdfViewer from '../ui/PdfViewer'
import { useProjects } from '../../contexts/ProjectsContext'
import {
  deleteReport,
  getReportDownloadUrl,
  listReports,
  type ReportListItem
} from '../../utils/reportsClient'
import { fetchSectionsWithVersion } from '../../utils/sectionsClient'
import PqrWeldPickerModal, {
  readWeldOptionsFromSections,
  type PqrWeldOption
} from './pqrPreview/PqrWeldPickerModal'
import PqrReportHistory from './pqrDetails/PqrReportHistory'
import type { PqrHistoryReport } from './pqrDetails/groupReportsForHistory'
import ConfirmDialog, { type ConfirmDialogTone } from '../ui/ConfirmDialog'
import {
  fetchImportSourcePdfUrl,
  isImportDraftReport,
  isPdfImportPipeSet,
  isUsableReportFileKey,
  triggerBrowserDownload
} from '../../utils/importReportPdf'

const PQR_TAG = 'PQR'

function isVisiblePqr(item: ReportListItem): boolean {
  if (item.tag !== PQR_TAG) return false
  if (typeof item.deletedAt === 'string' && item.deletedAt.trim().length > 0) return false
  return true
}

function extractionIdOf(report: ReportListItem): string {
  return typeof report.extractionId === 'string' ? report.extractionId.trim() : ''
}

export default function PqrDetails() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const { getProject } = useProjects()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)
  const [items, setItems] = useState<ReportListItem[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [viewer, setViewer] = useState<{ fileUrl: string; fileName: string } | null>(
    null
  )
  const [pageConfirm, setPageConfirm] = useState<{
    title: string
    description: string
    confirmLabel: string
    tone?: ConfirmDialogTone
    onConfirm: () => void
  } | null>(null)
  const [weldOptions, setWeldOptions] = useState<PqrWeldOption[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [isImportedPipeSet, setIsImportedPipeSet] = useState(false)
  const [legacyDownloadBusy, setLegacyDownloadBusy] = useState(false)
  const refresh = useCallback(async () => {
    if (!id || !pipeId) return
    setListLoading(true)
    try {
      const [reportsRes, sectionsRes] = await Promise.all([
        listReports({ projectId: id, pipeSetId: pipeId }),
        fetchSectionsWithVersion(id, pipeId).catch(() => ({
          sections: {} as Record<string, unknown>,
          version: 0
        }))
      ])
      const visible = (reportsRes.items || []).filter(isVisiblePqr)
      setItems(visible)
      setWeldOptions(readWeldOptionsFromSections(sectionsRes.sections))
      setIsImportedPipeSet(
        isPdfImportPipeSet(sectionsRes.sections.meta) ||
          visible.some((r) => isImportDraftReport(r))
      )
    } catch (err) {
      console.error('Failed to list PQR reports', err)
    } finally {
      setListLoading(false)
    }
  }, [id, pipeId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const openStoredPdf = useCallback(
    async (report: ReportListItem): Promise<{ downloadUrl: string; fileName: string }> => {
      if (!id || !pipeId) throw new Error('Missing project or pipe set')
      if (!isUsableReportFileKey(report.fileKey)) {
        throw new Error('This report has no PDF file yet')
      }
      const { downloadUrl } = await getReportDownloadUrl({
        projectId: id,
        pipeSetId: pipeId,
        reportId: report.id
      })
      return { downloadUrl, fileName: report.name }
    },
    [id, pipeId]
  )

  const handleDownloadLegacyUploaded = useCallback(async () => {
    if (!id || !pipeId || legacyDownloadBusy || busyId) return
    const source =
      items.find((r) => isImportDraftReport(r) && extractionIdOf(r)) ||
      items.find((r) => extractionIdOf(r))
    const extractionId = source ? extractionIdOf(source) : ''
    if (!source || !extractionId) {
      toast.error('Original PDF unavailable — re-import this WPS to view the source')
      return
    }
    setLegacyDownloadBusy(true)
    try {
      const pqrNumber =
        (typeof source.pqrNumber === 'string' && source.pqrNumber.trim()) || ''
      const revision =
        (typeof source.revision === 'string' && source.revision.trim()) || ''
      const fileName = `${pqrNumber || 'PQR'}${revision ? `-Rev-${revision}` : ''}.pdf`
      const { downloadUrl, fileName: resolvedName } = await fetchImportSourcePdfUrl({
        projectId: id,
        pipeSetId: pipeId,
        extractionId,
        kind: 'pqr',
        pqrNumber: pqrNumber || undefined,
        fileName
      })
      try {
        await triggerBrowserDownload(downloadUrl, resolvedName)
      } finally {
        URL.revokeObjectURL(downloadUrl)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not download original PDF')
    } finally {
      setLegacyDownloadBusy(false)
    }
  }, [id, pipeId, items, legacyDownloadBusy, busyId])

  const handlePreview = useCallback(
    (report: PqrHistoryReport) => {
      if (!project || !pipeSet) return
      if (busyId) return

      const params = new URLSearchParams()
      const ids =
        Array.isArray(report.weldIds) && report.weldIds.length > 0
          ? report.weldIds
          : typeof report.weldId === 'string' && report.weldId.trim().length > 0
            ? [report.weldId.trim()]
            : []
      if (ids.length > 0) {
        params.set('weldId', ids[0])
        if (ids.length > 1) {
          params.set('weldIds', ids.join(','))
        }
      }
      if (report.isSynthetic === true) {
        const seededPqrNumber =
          typeof report.pqrNumber === 'string' ? report.pqrNumber.trim() : ''
        const seededRevision =
          typeof report.revision === 'string' ? report.revision.trim() : ''
        if (seededPqrNumber && seededRevision) {
          params.set('new', '1')
          params.set('pqrNumber', seededPqrNumber)
          params.set('revision', seededRevision)
        }
      }
      const qs = params.toString()
      const url = `/projects/${project.id}/next/${pipeSet.id}/pqr/preview${qs ? `?${qs}` : ''}`
      navigate(url)
    },
    [navigate, project, pipeSet, busyId]
  )

  const handleGenerateApply = useCallback(
    (payload: {
      weldId: string
      weldIds: string[]
      pqrNumber: string
      revision: string
    }) => {
      if (!project || !pipeSet) return
      setPickerOpen(false)
      const weldIds =
        payload.weldIds && payload.weldIds.length > 0
          ? payload.weldIds
          : [payload.weldId]
      const params = new URLSearchParams({
        new: '1',
        weldId: weldIds[0],
        weldIds: weldIds.join(','),
        pqrNumber: payload.pqrNumber,
        revision: payload.revision
      })
      navigate(
        `/projects/${project.id}/next/${pipeSet.id}/pqr/preview?${params.toString()}`
      )
    },
    [navigate, project, pipeSet]
  )

  if (!project || !pipeSet) {
    return (
      <Card title="PQR not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  const handleDownload = async (report: ReportListItem) => {
    if (!id || !pipeId || busyId) return
    setBusyId(report.id)
    try {
      const { downloadUrl, fileName } = await openStoredPdf(report)
      setViewer({ fileUrl: downloadUrl, fileName })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open report')
    } finally {
      setBusyId(null)
    }
  }

  const performRemove = async (report: ReportListItem) => {
    if (!id || !pipeId) return
    setBusyId(report.id)
    const previous = items
    setItems(previous.filter((item) => item.id !== report.id))
    try {
      await deleteReport({ projectId: id, pipeSetId: pipeId, reportId: report.id })
      toast.success('PQR report removed')
    } catch (err) {
      setItems(previous)
      toast.error('Could not remove report')
    } finally {
      setBusyId(null)
    }
  }

  const handleRemove = async (
    report: ReportListItem,
    meta: { isLatest: boolean }
  ) => {
    if (!id || !pipeId) return
    const label = report.pqrNumber || report.name
    const revisionLabel = meta.isLatest
      ? 'latest revision'
      : `revision v${report.revision ?? ''}`
    setPageConfirm({
      title: meta.isLatest ? 'Remove latest revision?' : 'Remove revision?',
      description: `This will soft-delete the ${revisionLabel} of ${label}. It can be recovered if needed.`,
      confirmLabel: 'Remove',
      tone: 'danger',
      onConfirm: () => {
        setPageConfirm(null)
        void performRemove(report)
      }
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo={{ to: `/projects/${project.id}/next/${pipeSet.id}`, label: 'Back to WPS' }}
        title={<ProjectPageTitle label="PQR" projectName={project.name} />}
        subtitle={
          <>
            {formatClientLocationSubtitle(project) ? (
              <span>{formatClientLocationSubtitle(project)}</span>
            ) : null}
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {isImportedPipeSet ? (
              <Button
                variant="secondary"
                onClick={() => void handleDownloadLegacyUploaded()}
                disabled={legacyDownloadBusy || busyId != null}
                title="Download legacy uploaded PQR"
                data-testid="pqr-history-download-legacy"
              >
                Original PDF
              </Button>
            ) : null}
            <Button
              onClick={() => setPickerOpen(true)}
              data-testid="pqr-history-generate"
            >
              Generate PQR
            </Button>
          </div>
        }
      />

      <PqrReportHistory
        reports={items}
        weldOptions={weldOptions}
        busyId={busyId}
        listLoading={listLoading}
        onDownload={(r) => void handleDownload(r)}
        onRemove={(r, meta) => void handleRemove(r, meta)}
        onPreview={handlePreview}
      />

      {viewer && (
        <PdfViewer
          fileUrl={viewer.fileUrl}
          fileName={viewer.fileName}
          onClose={() => {
            if (viewer.fileUrl.startsWith('blob:')) {
              URL.revokeObjectURL(viewer.fileUrl)
            }
            setViewer(null)
          }}
        />
      )}

      <ConfirmDialog
        open={pageConfirm !== null}
        title={pageConfirm?.title ?? ''}
        description={pageConfirm?.description ?? ''}
        confirmLabel={pageConfirm?.confirmLabel ?? 'Confirm'}
        tone={pageConfirm?.tone}
        onConfirm={() => pageConfirm?.onConfirm()}
        onCancel={() => setPageConfirm(null)}
      />

      <PqrWeldPickerModal
        open={pickerOpen}
        welds={weldOptions}
        reports={items}
        initialWeldId={weldOptions[0]?.weldId ?? null}
        onApply={handleGenerateApply}
        onCancel={() => setPickerOpen(false)}
      />
    </div>
  )
}
